import React from 'react';

const STARS: Array<{ cx: number; cy: number; r: number; delay: number }> = [
  { cx: 80, cy: 48, r: 1.2, delay: 0 },
  { cx: 160, cy: 90, r: 0.9, delay: 0.8 },
  { cx: 260, cy: 36, r: 1.4, delay: 1.4 },
  { cx: 380, cy: 72, r: 1, delay: 0.3 },
  { cx: 520, cy: 28, r: 1.1, delay: 1.1 },
  { cx: 640, cy: 88, r: 0.8, delay: 1.9 },
  { cx: 760, cy: 42, r: 1.3, delay: 0.5 },
  { cx: 880, cy: 66, r: 1, delay: 1.6 },
  { cx: 1000, cy: 30, r: 1.2, delay: 0.9 },
  { cx: 1120, cy: 78, r: 0.9, delay: 2.2 },
  { cx: 1240, cy: 50, r: 1.1, delay: 0.2 },
  { cx: 1340, cy: 95, r: 0.8, delay: 1.3 },
];

function Book3D({ variant }: { variant: 'gold' | 'teal' }) {
  return (
    <div className={`login-3d-book login-3d-book--${variant}`}>
      <span className="login-3d-book__face login-3d-book__face--front">
        <span className="login-3d-book__line" />
        <span className="login-3d-book__line login-3d-book__line--short" />
        <span className="login-3d-book__line" />
        <span className="login-3d-book__line login-3d-book__line--mid" />
      </span>
      <span className="login-3d-book__face login-3d-book__face--back" />
      <span className="login-3d-book__face login-3d-book__face--spine" />
      <span className="login-3d-book__face login-3d-book__face--pages" />
      <span className="login-3d-book__face login-3d-book__face--top" />
    </div>
  );
}

function Cap3D() {
  return (
    <div className="login-3d-cap">
      <span className="login-3d-cap__board">
        <span className="login-3d-cap__board-face login-3d-cap__board-face--top" />
        <span className="login-3d-cap__board-face login-3d-cap__board-face--side" />
      </span>
      <span className="login-3d-cap__button" />
      <span className="login-3d-cap__tassel">
        <span className="login-3d-cap__cord" />
        <span className="login-3d-cap__knot" />
      </span>
      <span className="login-3d-cap__base" />
    </div>
  );
}

function Diploma3D() {
  return (
    <div className="login-3d-diploma">
      <span className="login-3d-diploma__roll login-3d-diploma__roll--left" />
      <span className="login-3d-diploma__sheet">
        <span className="login-3d-diploma__seal" />
        <span className="login-3d-diploma__line" />
        <span className="login-3d-diploma__line login-3d-diploma__line--short" />
      </span>
      <span className="login-3d-diploma__roll login-3d-diploma__roll--right" />
      <span className="login-3d-diploma__ribbon" />
    </div>
  );
}

function Notebook3D() {
  return (
    <div className="login-3d-notebook">
      <span className="login-3d-notebook__cover" />
      <span className="login-3d-notebook__spine" />
      <span className="login-3d-notebook__pages" />
      <span className="login-3d-notebook__band" />
    </div>
  );
}

/** Zero-dependency backdrop — dawn campus + lightweight CSS 3D education props. */
export function LoginBackground() {
  return (
    <div className="login-scene" aria-hidden>
      <div className="login-scene__sky" />
      <div className="login-scene__aurora" />
      <div className="login-scene__horizon" />
      <div className="login-scene__orb login-scene__orb--gold" />
      <div className="login-scene__orb login-scene__orb--teal" />
      <div className="login-scene__orb login-scene__orb--amber" />
      <div className="login-scene__orb login-scene__orb--deep" />
      <div className="login-scene__beam" />
      <div className="login-scene__rays" />
      <div className="login-scene__haze" />
      <div className="login-scene__hex" />
      <div className="login-scene__grid" />

      <svg className="login-scene__stars" viewBox="0 0 1440 200" preserveAspectRatio="xMidYMin slice">
        {STARS.map((star, i) => (
          <circle
            key={i}
            cx={star.cx}
            cy={star.cy}
            r={star.r}
            className="login-scene__star"
            style={{ animationDelay: `${star.delay}s` }}
          />
        ))}
      </svg>

      <div className="login-scene__stage">
        <div className="login-scene__float login-scene__float--book-a">
          <Book3D variant="gold" />
        </div>
        <div className="login-scene__float login-scene__float--book-b">
          <Book3D variant="teal" />
        </div>
        <div className="login-scene__float login-scene__float--cap">
          <Cap3D />
        </div>
        <div className="login-scene__float login-scene__float--diploma">
          <Diploma3D />
        </div>
        <div className="login-scene__float login-scene__float--notebook">
          <Notebook3D />
        </div>
      </div>

      <svg className="login-scene__campus" viewBox="0 0 1440 360" preserveAspectRatio="xMidYMax slice">
        <defs>
          <linearGradient id="campus-hill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(30, 58, 95, 0.55)" />
            <stop offset="55%" stopColor="rgba(18, 32, 58, 0.92)" />
            <stop offset="100%" stopColor="rgba(10, 16, 32, 1)" />
          </linearGradient>
          <linearGradient id="campus-building" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(56, 78, 120, 0.85)" />
            <stop offset="100%" stopColor="rgba(22, 32, 56, 0.98)" />
          </linearGradient>
          <linearGradient id="campus-accent" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(201, 162, 39, 0.35)" />
            <stop offset="100%" stopColor="rgba(40, 52, 80, 0.9)" />
          </linearGradient>
          <linearGradient id="campus-glow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(232, 212, 139, 0.2)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>

        {/* Soft ground glow behind skyline */}
        <ellipse cx="720" cy="300" rx="620" ry="70" fill="url(#campus-glow)" />

        {/* Distant hills */}
        <path
          fill="url(#campus-hill)"
          d="M0 360 L0 220 Q180 170 360 200 Q540 240 720 190 Q900 140 1080 195 Q1260 245 1440 210 L1440 360 Z"
        />

        {/* Left wing */}
        <g fill="url(#campus-building)">
          <rect x="120" y="168" width="150" height="140" rx="3" />
          <rect x="145" y="148" width="100" height="24" rx="2" />
          <polygon points="195,118 130,148 260,148" fill="rgba(201,162,39,0.22)" />
        </g>
        <g fill="rgba(232,212,139,0.55)">
          <rect x="140" y="190" width="10" height="14" rx="1" />
          <rect x="160" y="190" width="10" height="14" rx="1" />
          <rect x="180" y="190" width="10" height="14" rx="1" />
          <rect x="200" y="190" width="10" height="14" rx="1" />
          <rect x="220" y="190" width="10" height="14" rx="1" />
          <rect x="140" y="220" width="10" height="14" rx="1" opacity="0.7" />
          <rect x="160" y="220" width="10" height="14" rx="1" opacity="0.45" />
          <rect x="180" y="220" width="10" height="14" rx="1" />
          <rect x="200" y="220" width="10" height="14" rx="1" opacity="0.6" />
          <rect x="220" y="220" width="10" height="14" rx="1" />
          <rect x="140" y="250" width="10" height="14" rx="1" opacity="0.5" />
          <rect x="180" y="250" width="10" height="14" rx="1" />
          <rect x="220" y="250" width="10" height="14" rx="1" opacity="0.75" />
        </g>

        {/* Center hall */}
        <g fill="url(#campus-accent)">
          <rect x="520" y="130" width="220" height="178" rx="4" />
          <rect x="575" y="95" width="110" height="40" rx="2" />
          <polygon points="630,58 500,98 760,98" fill="rgba(232,212,139,0.28)" />
          <rect x="610" y="250" width="40" height="58" rx="2" fill="rgba(18,28,48,0.85)" />
        </g>
        <circle cx="630" cy="72" r="7" fill="rgba(232,212,139,0.65)" />
        <line x1="630" y1="72" x2="630" y2="42" stroke="rgba(232,212,139,0.45)" strokeWidth="2" />
        <g fill="rgba(255,236,170,0.65)">
          <rect x="545" y="155" width="14" height="18" rx="1" />
          <rect x="575" y="155" width="14" height="18" rx="1" opacity="0.55" />
          <rect x="605" y="155" width="14" height="18" rx="1" />
          <rect x="645" y="155" width="14" height="18" rx="1" opacity="0.7" />
          <rect x="675" y="155" width="14" height="18" rx="1" />
          <rect x="705" y="155" width="14" height="18" rx="1" opacity="0.5" />
          <rect x="545" y="195" width="14" height="18" rx="1" opacity="0.6" />
          <rect x="575" y="195" width="14" height="18" rx="1" />
          <rect x="605" y="195" width="14" height="18" rx="1" opacity="0.45" />
          <rect x="645" y="195" width="14" height="18" rx="1" />
          <rect x="675" y="195" width="14" height="18" rx="1" opacity="0.8" />
          <rect x="705" y="195" width="14" height="18" rx="1" />
        </g>

        {/* Right wing */}
        <g fill="url(#campus-building)">
          <rect x="980" y="175" width="170" height="133" rx="3" />
          <rect x="1015" y="155" width="100" height="24" rx="2" />
          <polygon points="1065,128 990,155 1140,155" fill="rgba(120,180,220,0.2)" />
          <rect x="1160" y="200" width="90" height="108" rx="2" />
        </g>
        <g fill="rgba(180,220,255,0.5)">
          <rect x="1005" y="200" width="12" height="15" rx="1" />
          <rect x="1030" y="200" width="12" height="15" rx="1" opacity="0.65" />
          <rect x="1055" y="200" width="12" height="15" rx="1" />
          <rect x="1080" y="200" width="12" height="15" rx="1" opacity="0.45" />
          <rect x="1005" y="230" width="12" height="15" rx="1" opacity="0.55" />
          <rect x="1030" y="230" width="12" height="15" rx="1" />
          <rect x="1055" y="230" width="12" height="15" rx="1" opacity="0.7" />
          <rect x="1080" y="230" width="12" height="15" rx="1" />
          <rect x="1180" y="220" width="10" height="12" rx="1" />
          <rect x="1200" y="220" width="10" height="12" rx="1" opacity="0.6" />
          <rect x="1180" y="245" width="10" height="12" rx="1" opacity="0.5" />
          <rect x="1200" y="245" width="10" height="12" rx="1" />
        </g>

        {/* Trees / soft foliage silhouettes */}
        <g fill="rgba(28, 48, 72, 0.85)">
          <ellipse cx="90" cy="290" rx="28" ry="36" />
          <rect x="86" y="300" width="8" height="40" rx="2" />
          <ellipse cx="310" cy="300" rx="22" ry="30" />
          <rect x="307" y="310" width="6" height="30" rx="2" />
          <ellipse cx="860" cy="295" rx="26" ry="34" />
          <rect x="857" y="305" width="7" height="35" rx="2" />
          <ellipse cx="1320" cy="300" rx="30" ry="38" />
          <rect x="1316" y="312" width="8" height="32" rx="2" />
        </g>

        {/* Foreground ground */}
        <path
          fill="rgba(10, 16, 32, 0.95)"
          d="M0 330 Q360 300 720 318 Q1080 336 1440 308 L1440 360 L0 360 Z"
        />
      </svg>

      <div className="login-scene__vignette" />
    </div>
  );
}
